import { ProductFaq } from "../types";
import { settingsMarkers, soundMarkers, v1 } from "./protect-v1.shared";

export const protectV1EN: ProductFaq = {
	title: "Support and troubleshooting",
	downloads: [
		{ group: "English", title: "Quick Start Guide", path: "/documents/downloads/2024-02-19_QuickStart_007570_R1B.pdf" },
		{ group: "English", title: "Reference Guide", path: "/documents/downloads/2024-06-07_ReferenceGuide_EN_007562-R1C.pdf" },
		{ group: "English", title: "Frequently asked questions and Troubleshooting", path: "/documents/downloads/2024-06-10_EarsOnly_Protect-FAQ+Troubleshooting-EN.pdf" },
		{ group: "Nederlands", title: "Snel Start Gids", path: "/documents/downloads/2024-03-04_SnelStartGids_007561-R1B.pdf" },
		{ group: "Nederlands", title: "Referentiegids", path: "/documents/downloads/2024-06-07_ReferentieGids_NL_007562-R1C.pdf" },
		{ group: "Nederlands", title: "Veelgestelde vragen en Probleemoplossing", path: "/documents/downloads/2024-06-27_EarsOnly_Protect-FAQ+Troubleshooting-NL.pdf" },
	],
	categories: [
		{
			id: "pairing",
			title: "Pairing",
			questions: [
				{
					id: "pair",
					title: "How do I pair my EarsOnly Protect to a device?",
					blocks: [
						{ t: "p", text: "Download the MyEarsOnly app by scanning the QR-code below or from the [Google Play Store or Apple App Store](https://app.earsonly.nl/download). Follow the instructions in the app, or manually pair EarsOnly Protect with a device as explained below:" },
						{ t: "img", src: v1("scan_QRcode"), alt: "Scan the QR-code to download the MyEarsOnly app" },
						{
							t: "ol",
							items: [
								"Ensure that the Charging Case is charged to at least 25%.",
							],
						},
						{ t: "note", kind: "tip", text: "Go to {{q:battery}} for guidance on checking the battery levels." },
						{
							t: "ol",
							start: 2,
							items: [
								"Make sure the Earbuds are properly placed in the Charging Case.",
								"Press and hold the button on the Charging Case for 3 seconds. Wait until all three lights blink; this may take up to 10 seconds.",
								"Open the Bluetooth® menu on your device and select “Connect new device” (this may vary by device, refer to the respective manual).",
								"When all three lights on the Charging Case blink, “EarsOnly Protect” will appear in the list.",
								"Select “EarsOnly Protect” from the list to connect.",
							],
						},
						{ t: "img", src: v1("pairing"), alt: "Pairing the Earbuds with your device" },
					],
				},
			],
		},
		{
			id: "battery-category",
			title: "Battery",
			questions: [
				{
					id: "battery",
					title: "How do I check the battery levels and charge the batteries?",
					blocks: [
						{
							t: "ol",
							items: [
								"Open the lid of the Charging Case.",
								"Check the battery status of the Charging Case, indicated by the middle LED (Figure 1).",
								"When the battery is low, connect the Charging Case using the provided USB-C cable and a USB charger port or place the case on a Qi compatible wireless charge pad.",
							],
						},
						{ t: "img", src: v1("case_battery_light"), alt: "The middle LED of the Charging Case shows its battery status", caption: "Figure 1" },
						{
							t: "ol",
							start: 4,
							items: [
								"Place the Earbud (left or right) on the corresponding charging contacts (Figure 2).",
								"When the Earbud is placed in the Charging Case, the corresponding left or right light will briefly illuminate to indicate a good connection (Figure 2).",
								"Repeat for the other Earbud.",
							],
						},
						{ t: "note", kind: "warning", text: "Ensure that the charging contacts of both the Earbuds and the Charging Case are free from dirt (see: {{q:clean}}). If there is poor or no contact, the Earbuds will not start charging." },
						{ t: "img", src: v1("right_earbud_in_case"), alt: "An Earbud placed on the charging contacts", caption: "Figure 2" },
						{ t: "ol", start: 7, items: ["Close the lid of the Charging Case."] },
						{ t: "note", kind: "note", text: "Optional: briefly press the button on the Charging Case (Figure 3) to view the current battery status of the Earbuds and the Charging Case. The lights will turn off after a few seconds to save energy." },
						{ t: "img", src: v1("pairing"), alt: "The button on the Charging Case", caption: "Figure 3" },
						{ t: "img", src: v1("earbuds_in_case"), alt: "The three LEDs on the Charging Case", caption: "Figure 4" },
						{
							t: "table",
							head: ["LED 1,2,3 (Figure 4)", "Description"],
							rows: [
								["On", "Earbud/Charging Case is more than 25% charged"],
								["On (Orange)", "Earbud/Charging Case is less than 25% charged"],
								["On (Red)", "Earbud/Charging Case is less than 10% charged"],
								["Off", "Charging Case battery is empty, or there is no connection between Earbud(s) and Charging Case charging contacts (lights turn off after a few seconds to save battery)"],
								["Fade Animation", "Charging Case is charging (when the lid is closed, lights turn off after a few seconds)"],
							],
							caption: "Table 1: Battery level",
						},
					],
				},
			],
		},
		{
			id: "maintenance",
			title: "Cleaning & Maintenance",
			questions: [
				{
					id: "clean",
					title: "How do I clean my EarsOnly Protect?",
					blocks: [
						{ t: "note", kind: "warning", text: "Do not use any aggressive cleaning agents or solvents, like benzene, thinner, dishwashing liquid or alcohol as they may damage the surface. Contact the manufacturer for additional information." },
						{ t: "h", text: "Earbuds" },
						{ t: "p", text: "Wipe the outer surface of the Earbuds with a lightly dampened cloth. Ensure that the charging contacts are free from dirt." },
						{ t: "img", src: v1("cleaning_earbuds"), alt: "Cleaning the Earbuds" },
						{ t: "h", text: "Charging Case" },
						{ t: "p", text: "Wipe the inside and outside of the Charging Case with a lightly dampened cloth. Ensure that the charging contacts are free from dirt." },
						{ t: "img", src: v1("cleaning_case"), alt: "Cleaning the Charging Case" },
						{ t: "h", text: "Wax guards" },
						{ t: "p", text: "Periodic replacement of the wax guards is required to ensure correct functioning. Contact your supervisor or reseller when the wax guards are highly polluted or damaged." },
						{ t: "img", src: v1("cleaning_filter"), alt: "The wax guard of an Earbud" },
						{ t: "note", kind: "note", text: "Make sure that the Earbuds and Charging Case are fully dry before placing the Earbuds into the Charging Case." },
					],
				},
			],
		},
		{
			id: "app",
			title: "MyEarsOnly app",
			questions: [
				{
					id: "app-overview",
					title: "MyEarsOnly app",
					blocks: [
						{
							t: "annotated",
							image: v1("app-sound-settings"),
							alt: "The sound screen of the MyEarsOnly app",
							width: 303,
							height: 712,
							items: [
								{ icon: "A", title: "Connection", text: "Indicates if your Earbuds and Charging Case is connected to your device.", ...soundMarkers.A },
								{ icon: "B", title: "Battery status", text: "Provides real-time battery levels of your Earbuds and Charging Case.", ...soundMarkers.B },
								{ icon: "Microphone", title: "Microphone", text: "When in a call, the microphones of this side will be used." },
								{ icon: "Charging", title: "Charging", text: "Indicates whether the Earbud or Charging Case is charging." },
								{ icon: "C", title: "Percentage", text: "Battery percentage of Earbud or Charging Case.", ...soundMarkers.C },
								{ icon: "D", title: "Sound/Settings", text: "Switch between sound configurations and use Settings.", ...soundMarkers.D },
								{ icon: "E", title: "Hearing protection", text: "Switch between the various hearing protection modes.", ...soundMarkers.E },
								{ icon: "F", title: "Audio balance", text: "Adjust and fine-tune the audio balance in each ear.", ...soundMarkers.F },
								{ icon: "G", title: "Music Equalizer", text: "Select an audio profile to match your preferred genre of music.", ...soundMarkers.G },
							],
						},
					],
				},
				{
					id: "app-settings",
					title: "App settings",
					blocks: [
						{
							t: "annotated",
							image: v1("app-settings"),
							alt: "The settings screen of the MyEarsOnly app",
							width: 304,
							height: 712,
							items: [
								{ icon: "A", title: "Button configuration", text: "Adjust the functions assigned to the tap and press buttons on each earbud.", ...settingsMarkers.A },
								{ icon: "B", title: "Software update", text: "Update your Earbuds and Charging Case.", ...settingsMarkers.B },
								{ icon: "C", title: "Use instructions, FAQ and Troubleshooting", ...settingsMarkers.C },
							],
						},
					],
				},
			],
		},
		{
			id: "troubleshooting",
			title: "Troubleshooting",
			questions: [
				{
					id: "reset",
					title: "Reset Earbuds & Reset Charging case",
					blocks: [
						{ t: "h", text: "Reset Earbuds" },
						{
							t: "ol",
							items: [
								"Take the Earbuds out of the Charging case.",
								"Press and hold the Earbud buttons for 15 seconds.",
								"Place them back in the Charging case and wait 10 seconds.",
							],
						},
						{ t: "note", kind: "note", text: "(Preferred) settings will not be lost." },
						{ t: "h", text: "Reset Charging case" },
						{ t: "ol", items: ["Press and hold the Charging case button for 15 seconds."] },
					],
				},
				{
					id: "not-charged",
					title: "After a long charge my Earbuds aren’t charged/fully charged",
					blocks: [
						{
							t: "ol",
							items: [
								"Make sure the Earbuds are properly placed inside the Charging case.",
								"Make sure the Charging case still has charge (see: {{q:battery}})",
								"Clean both the charging contacts of the Earbud and the Charging case with a damp cloth. Place them back in the Charging case and check if the LEDs light up.",
								"If this doesn’t work perform a reset of the Earbuds and Charging case (see: {{q:reset}}) and try again.",
							],
						},
					],
				},
				{
					id: "no-audio",
					title: "Earbud does not stream audio",
					blocks: [
						{
							t: "ol",
							items: [
								"Make sure the Earbuds are connected to the audio source.",
								"Make sure the audio stream is playing.",
								"Place Earbud back in Charging case, wait 10 seconds and take them out again.",
								"If the audio stream stopped playing, start the audio stream again.",
								"Still no audio? Perform a reset of the Earbuds and try again (see: {{q:reset}}).",
							],
						},
					],
				},
				{
					id: "wont-pair",
					title: "The Earbuds won’t pair to my device",
					blocks: [
						{
							t: "ol",
							items: [
								"Make sure the Charging case has been charged and is at least above 25%.",
								"Make sure the Earbuds are properly placed inside the Charging case.",
								{
									text: "Check if both Earbuds are connected to the Charging case, press the Charging case button. Check if all LEDs light up.",
									sub: [
										"If all LEDs light up, follow the pairing instructions (see: {{q:pair}}).",
										"If a LED does not light up, the corresponding Earbud is not connected to the Charging case. Pairing won’t work: clean both the charging contacts of the Earbud and the Charging case with a damp cloth. Place the Earbud(s) back in the Charging case and check if the LEDs light up. If so, follow the pairing instructions (see: {{q:pair}}).",
									],
								},
								"If this doesn’t work perform a reset of the Earbuds and Charging case and try again (see: {{q:reset}}).",
							],
						},
					],
				},
				{
					id: "wont-connect",
					title: "The Earbuds won’t connect to my device",
					blocks: [
						{
							t: "ol",
							items: [
								"Place the Earbuds back in the Charging case, wait 10 seconds and take them out again.",
								"Make sure the Earbuds are paired with your device, if this is not the case follow the pairing instructions (see: {{q:pair}}).",
								"If this doesn’t work, perform a reset of the Earbuds (see: {{q:reset}}) and try again.",
								"Reboot your device you are trying to connect to and try again.",
								"If this doesn’t work, remove the Earbuds from the Bluetooth pairing list on your device and re-pair the Earbuds to your device (see: {{q:pair}}).",
							],
						},
					],
				},
				{
					id: "out-of-sync",
					title: "Audio is out of sync",
					blocks: [
						{
							t: "ol",
							items: [
								"Place the Earbuds in the Charging case, wait 10 seconds and take them out again.",
								"If this doesn’t work perform a reset of the Earbuds and try again (see: {{q:reset}}).",
							],
						},
					],
				},
				{
					id: "factory-reset",
					title: "Factory reset",
					blocks: [
						{ t: "note", kind: "warning", text: "By returning to factory settings you will lose the (preferred) settings and the pairing information with your devices." },
						{
							t: "ol",
							items: [
								"Make sure the Charging case has more than 25% charge.",
								"Make sure the Earbuds are properly placed inside the Charging case.",
								"Press and hold the button on the Charging Case for 3 seconds. Wait until all three lights blink; this may take up to 10 seconds.",
								"Quickly double press the Charging case button. The LEDs should now blink in a different colour.",
								"When the LEDs blink orange again the factory reset is successful. You can now pair your EarsOnly Protect to a device again.",
							],
						},
					],
				},
			],
		},
	],
};
